import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { PropsValue } from 'react-select';

import type { OptionType } from '@hivebrite/design-system/Inputs';
import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { getCategories } from 'hb-react/backoffice/components/CategoriesList/requests';
import { makeConfigUrls } from 'hb-react/backoffice/components/CategoriesList/utils/configUrls';
import {
  CATEGORIES_SEARCH_DEBOUNCE_TIME,
  PER_PAGE,
  CATEGORIES_QUERY_STALE_TIME,
} from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/constants';
import { useConvertCategoriesValueToOptions } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/hooks/useConvertCategoriesValueToOptions';
import type { ReactSelectCategoriesProps } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/types';
import { categoriesToOptions } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/utils/categoriesToOptions';

export const ReactSelectCategories = ({
  categorizedType,
  isMulti,
  label = __('Filter by category'),
  networkId,
  onChange,
  placeholder = __('Search and select a category'),
  topicId,
  availableForNetwork,
  value,
  ...otherReactSelectProps
}: ReactSelectCategoriesProps) => {
  const queryClient = useQueryClient();

  const [isAsync, setIsAsync] = useState(false);

  const { data: defaultOptions = [], isLoading: isLoadingDefaultOptions } =
    useQuery({
      queryKey: [networkId, topicId, 'categories', categorizedType],
      queryFn: async ({ signal }) => {
        const configUrls = makeConfigUrls({
          networkId,
          type: categorizedType,
          topicId,
          availableForNetwork,
          page: 1,
          hasPagination: true,
          perPage: PER_PAGE,
        });

        const { data, total } = await getCategories(configUrls, signal);

        // This is only available for categorizedTypes that use search_and_paginate in the backend
        // Check the `index` action of app/controllers/web/backoffice/networks/network_categories_controller.rb
        // If there is more categories than the data loaded,
        // we switch to async mode to allow the user to search
        // and retrieve any category
        if (total && total > data.length) {
          setIsAsync(true);
        }

        const matchedOptions = categoriesToOptions(data);

        addOptionsToCache(matchedOptions);

        return matchedOptions;
      },
      staleTime: CATEGORIES_QUERY_STALE_TIME,
    });

  const {
    isLoading: isLoadingValueAsOptions,
    options: valueAsOptions,
    addOptionsToCache,
  } = useConvertCategoriesValueToOptions({
    networkId,
    categorizedType,
    topicId,
    value,
    enabled: !isLoadingDefaultOptions,
  });

  const inputChangeAbortController = useRef<AbortController | null>(null);
  const handleInputChange = useCallback(
    (textSearchValue: string) => {
      inputChangeAbortController.current?.abort();
      inputChangeAbortController.current = new AbortController();

      return new Promise<OptionType[]>((resolve, reject) => {
        const timeoutId = setTimeout(async () => {
          try {
            // react-select caching is not playing well with our "debounced" approach
            // so we disable it (cacheOptions={false}) and use react-query's caching
            const { data } = await queryClient.fetchQuery({
              queryKey: [
                networkId,
                topicId,
                'categories',
                categorizedType,
                textSearchValue,
              ],
              queryFn: ({ signal }) => {
                const configUrls = makeConfigUrls({
                  networkId,
                  type: categorizedType,
                  topicId,
                  availableForNetwork,
                  page: 1,
                  hasPagination: true,
                  perPage: PER_PAGE,
                  searchKeyword: textSearchValue,
                });

                // Create a combined abort controller for backward compatibility
                // since AbortSignal.any() is not available in older browsers
                const combinedAbortController = new AbortController();

                const signals = [
                  signal,
                  inputChangeAbortController.current?.signal,
                ].filter(Boolean);

                signals.forEach((abortSignal) => {
                  if (abortSignal?.aborted) {
                    combinedAbortController.abort();

                    return;
                  }

                  abortSignal?.addEventListener('abort', () => {
                    combinedAbortController.abort();
                  });
                });

                return getCategories(
                  configUrls,
                  combinedAbortController.signal,
                );
              },
              staleTime: CATEGORIES_QUERY_STALE_TIME,
            });

            const matchedOptions = categoriesToOptions(data);

            addOptionsToCache(matchedOptions);

            resolve(matchedOptions);
          } catch (error) {
            reject(error);
          }
        }, CATEGORIES_SEARCH_DEBOUNCE_TIME);

        inputChangeAbortController.current?.signal.addEventListener(
          'abort',
          () => {
            reject(new Error('Search aborted'));
            clearTimeout(timeoutId);
          },
        );
      });
    },
    [queryClient, networkId, topicId, categorizedType, addOptionsToCache],
  );

  const valueToUse = useMemo(() => {
    if (!valueAsOptions) {
      return null;
    }

    if (Array.isArray(valueAsOptions) && !isMulti) {
      return valueAsOptions[0] ?? null;
    }

    return valueAsOptions ?? null;
  }, [isMulti, valueAsOptions]);

  return (
    <ReactSelectInput
      defaultOptions={defaultOptions}
      isAsync={isAsync}
      cacheOptions={false}
      options={isAsync ? undefined : defaultOptions}
      label={label}
      isMulti={isMulti}
      isLoading={isLoadingValueAsOptions || isLoadingDefaultOptions}
      placeholder={placeholder}
      loadOptions={handleInputChange}
      onChange={onChange as (selectedOption: PropsValue<OptionType>) => void}
      value={valueToUse}
      {...otherReactSelectProps}
    />
  );
};

export default ReactSelectCategories;
